import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { useFamily } from '@/context/FamilyContext'
import { useStorytelling } from '@/context/StorytellingContext'
import { getChapter, chapterMap } from '@/data/chapters'
import { getLessonState, loadLessonResponses, recordResponse } from '@/services/lessonEngine'
import { StepFeel } from '@/components/storytelling/StepFeel'
import { StepShape } from '@/components/storytelling/StepShape'
import { StepSee } from '@/components/storytelling/StepSee'
import { StepSecret } from '@/components/storytelling/StepSecret'
import type { LessonStep } from '@/types/storytelling'

export function LessonPage() {
  const { lessonId } = useParams<{ lessonId: string }>()
  const navigate = useNavigate()
  const { activeWriter } = useFamily()
  const { completeChapter, unlockSecret } = useStorytelling()

  const chapter = lessonId ? getChapter(lessonId) : undefined

  const [responses, setResponses] = useState<{ step: number; choiceMade: string }[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    async function load() {
      if (!activeWriter || !lessonId) return
      const data = await loadLessonResponses(activeWriter.id, lessonId)
      setResponses(data)
      setIsLoading(false)
    }
    load()
  }, [activeWriter, lessonId])

  const handleStepComplete = useCallback(
    async (stepIndex: number, choiceMade: string, responseData: Record<string, unknown> = {}) => {
      if (!activeWriter || !lessonId) return
      await recordResponse(activeWriter.id, lessonId, stepIndex, choiceMade, responseData)
      setResponses((prev) => [...prev, { step: stepIndex, choiceMade }])
    },
    [activeWriter, lessonId]
  )

  const handleSecretUnlock = useCallback(async () => {
    if (!chapter || !lessonId) return
    await handleStepComplete(3, 'unlocked')
    await unlockSecret(chapter.secret, chapter.id)
    await completeChapter(chapter.id)
  }, [chapter, lessonId, handleStepComplete, unlockSecret, completeChapter])

  if (!chapter) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <p style={{ color: 'var(--color-story-text-muted)' }}>Chapter not found.</p>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--color-story-primary)' }} />
      </div>
    )
  }

  const state = getLessonState(chapter, responses)

  if (state.isComplete) {
    return (
      <div>
        <StepSecret step={chapter.steps[3]} secret={chapter.secret} onUnlock={() => {}} />
        <div className="text-center py-6">
          <button onClick={() => navigate('/storytelling')} className="px-6 py-3 rounded-xl text-sm font-semibold text-white" style={{ background: 'linear-gradient(135deg, var(--color-story-primary), var(--color-story-primary-light))' }}>Back to Your Journey</button>
        </div>
      </div>
    )
  }

  const stepDots = (
    <div className="flex justify-center gap-2 py-4 bg-white" style={{ borderBottom: '1px solid var(--color-story-bg)' }}>
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="w-2.5 h-2.5 rounded-full" style={{ background: i <= state.currentStepIndex ? 'var(--color-story-primary)' : 'var(--color-story-border)' }} />
      ))}
    </div>
  )

  const chapterIndex = chapterMap.findIndex((c) => c.id === chapter.id)

  const renderStep = (step: LessonStep, index: number) => {
    switch (step.type) {
      case 'feel':
        return <StepFeel step={step} onComplete={(choice) => handleStepComplete(index, choice)} />
      case 'shape':
        return <StepShape step={step} onComplete={(choice) => handleStepComplete(index, choice)} />
      case 'see': {
        const shapeResponse = responses.find((r) => r.step === 1)
        return <StepSee step={step} choiceMade={shapeResponse?.choiceMade ?? 'b'} onComplete={() => handleStepComplete(index, 'acknowledged')} />
      }
      case 'secret':
        return <StepSecret step={step} secret={chapter.secret} onUnlock={handleSecretUnlock} />
      default:
        return null
    }
  }

  return (
    <div>
      <div className="px-5 py-4" style={{ background: 'linear-gradient(135deg, var(--color-story-primary), var(--color-story-primary-light))' }}>
        <div className="max-w-lg mx-auto flex justify-between items-center">
          <div>
            <p className="text-xs text-white/70 uppercase tracking-wide">Chapter {chapterIndex + 1}</p>
            <p className="text-lg font-bold text-white">{chapter.title}</p>
          </div>
          <span className="text-xs text-white/60 bg-white/15 px-3 py-1 rounded-full">Step {state.currentStepIndex + 1} of 4</span>
        </div>
      </div>
      {stepDots}
      <div className="max-w-lg mx-auto">{renderStep(state.currentStep, state.currentStepIndex)}</div>
    </div>
  )
}
